import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { CartProvider } from "./context/CartContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CartDrawer } from "./components/CartDrawer";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Contact from "./pages/Contact";
import About from "./pages/About";
import NotFound from "./pages/NotFound";

// Scroll to hash or top on route change
const ScrollHandler = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);
  return null;
};

// Dynamic per-page SEO — title + descriptions follow route and language
const Seo = () => {
  const location = useLocation();
  const { lang, t } = useLanguage();
  useEffect(() => {
    const s = t.seo;
    let title = s.siteTitle;
    let description = s.description;
    let shareDescription = s.shareDescription;
    const p = location.pathname;
    if (p === "/") {
      if (location.hash === "#cabo") title = s.cabo;
      else if (location.hash === "#mindset") title = s.mindset;
    } else if (p.startsWith("/product/")) title = s.product;
    else if (p === "/shop") title = s.shop;
    else if (p === "/about") {
      title = s.about;
      description = s.aboutDescription;
      shareDescription = s.aboutDescription;
    } else if (p === "/contact") title = s.contact;
    else if (p === "/checkout") title = s.checkout;
    else if (p === "/order-success") title = s.orderSuccess;
    document.title = title;
    document.documentElement.lang = lang;
    const setMeta = (sel, content) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', shareDescription);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', shareDescription);
  }, [location, lang, t]);
  return null;
};

export default function App() {
  return (
    <LanguageProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollHandler />
          <Seo />
          <div className="App bg-black text-white min-h-screen flex flex-col">
            <Header />
            <CartDrawer />
            <div className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-success" element={<OrderSuccess />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
            <Footer />
          </div>
          <Toaster
            theme="dark"
            position="top-center"
            toastOptions={{
              style: {
                background: "#000",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 0,
              },
            }}
          />
        </BrowserRouter>
      </CartProvider>
    </LanguageProvider>
  );
}
